import React from 'react';
// utils
import { PubSub } from '../../utils/eventUtils';
import {
  determineIsAccountEligibleForQuote,
  pullAttestationDataFromAccount,
  useQuoteCreationFunctions,
} from '../accountUtils';
import { manageAPIError } from '../../utils';
import { Modal } from '../../components/modals/v2/helpers/v2.modal.helpers';
// actions
import { certifyAccount } from '../AccountService';
import { checkStatusOnAccount } from '../../inbox/QuotesService';
import { FlowTypes } from '../../types/flows';
import { toUniversalUtcDate } from '../../utils/date.utils';
import { StopRenewalQuoteConfig } from '../../console/renewals/agency/StopRenewalQuote';
import { useAPIErrorHandler } from '../../components/hooks/useAPIErrorHandler';
import usePlatformRegion from '../../utils/hooks/usePlatformRegion';
import { ProductTypes } from '../../types';
import { useActor } from '../../components/hooks/useActor';
import useNotifications from '../../_lib/notifications/useNotifications';

export const useAccountAttestation = ({
  account = {},
  renewal = false,
  onNewBusinessRedirect = handleNewBusinessRedirect,
  onRenewalRedirect = handleRenewalRedirect,
  onUnavailableRedirect = handleUnavailableRedirect,
  onAttestFailed = handleAttestFailed,
}) => {
  const handleAPIError = useAPIErrorHandler();
  const region = usePlatformRegion();
  // @ts-expect-error TS(2339): Property 'isVerifiedByAgent' does not exist on typ... Remove this comment to see the full error message
  const { isVerifiedByAgent, verifiedDate, id } = account;
  const actor = useActor();

  const notifications = useNotifications();

  // state
  const [checkIfVerifiedByAgent, setCheckIfVerifiedByAgent] =
    React.useState(isVerifiedByAgent);
  const [agentVerifiedDate, setAgentVerifiedDate] = React.useState(() =>
    toUniversalUtcDate(verifiedDate)
  );
  const [disabledForUnsavedChanges, setDisableForUnsavedChanges] =
    React.useState(false);

  // hooks
  const { shouldQuotingBeDisabledForAccountError, error } =
    determineIsAccountEligibleForQuote(account, checkIfVerifiedByAgent);
  const { detemineAgentOnAttestation } = useQuoteCreationFunctions();

  React.useEffect(() => {
    const sub = PubSub.subscribe('disable-Submit', (value: any) => {
      setDisableForUnsavedChanges(value);
    });

    const verify = PubSub.subscribe('org:agent-attested', (value: any) => {
      setCheckIfVerifiedByAgent(value.isVerifiedByAgent);
    });

    return () => {
      sub.remove();
      verify.remove();
    };
    // eslint-disable-next-line
  }, []);

  const handleAttestment = React.useCallback(() => {
    if (checkIfVerifiedByAgent) {
      return null;
    }

    const payload = {
      ...detemineAgentOnAttestation(),
      ...pullAttestationDataFromAccount(account),
    };

    certifyAccount(payload)
      .then(() => {
        setCheckIfVerifiedByAgent(true);
        setAgentVerifiedDate(toUniversalUtcDate(new Date()));
      })
      .catch((error) => {
        onAttestFailed(error, notifications);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIfVerifiedByAgent, account, notifications]);

  const handleCheckStatusOnAccount = (agencyId: any, accountId: any) => {
    checkStatusOnAccount({
      accountId: id || accountId,
      agencyId,
    })
      .then(({ data }: any) => {
        const { quoteFlowType } = data;
        if (
          (region.isUK || data.productType === ProductTypes.p100_pro) &&
          actor.isCowbell
        ) {
          onNewBusinessRedirect(data, notifications);
        } else if (quoteFlowType === FlowTypes.newBusiness) {
          onNewBusinessRedirect(data, notifications);
        } else if (quoteFlowType === FlowTypes.unavailable) {
          onUnavailableRedirect(data);
        } else if (quoteFlowType === FlowTypes.renewal) {
          // @ts-expect-error TS(2554): Expected 2 arguments, but got 3.
          onRenewalRedirect(data, accountId, notifications);
        }
      })
      .catch(
        handleAPIError('Cannot quote at this time. Please try again later')
      );
  };

  return {
    checkIfVerifiedByAgent,
    agentVerifiedDate,
    shouldQuotingBeDisabledForAccountError,
    disabledForUnsavedChanges,
    disableSubmit:
      shouldQuotingBeDisabledForAccountError || disabledForUnsavedChanges,
    errorMessage: error,
    handleAttestment,
    handleCheckStatusOnAccount,
  };
};

const handleNewBusinessRedirect = (data: any, notifications: any) => {
  notifications.error('Need a function to move ahead');
};

const handleRenewalRedirect = (data: any, notifications: any) => {
  notifications.error('Need a function to move ahead');
};

const handleUnavailableRedirect = (data: any) => {
  Modal.show(StopRenewalQuoteConfig, {
    data: { ...data },
  });
};

const handleAttestFailed = (error: any, notifications: any) => {
  return notifications.error(
    manageAPIError(
      error,
      'Something went wrong while verifying the organization profile.'
    )
  );
};
